import { CloseOutlined, EditOutlined, SearchOutlined, UndoOutlined } from '@ant-design/icons';
import { Button, Input, Popconfirm, Space } from 'antd';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { PatternFormat } from 'react-number-format';

import {
  useCarrierCreate,
  useCarrierRemove,
  useCarrierUpdate,
  useTerminalCreate,
} from '../../hooks/apiHooks';
import Container from '../common/Container';
import EditButton from '../common/EditButton';
import PanelHeader from '../common/PanelHeader';
import CarrierModal from '../modals/CarrierModal';
import CPTable from '../tables/CPTable';

/*
interface ICarrierListProps {
  id?: string;
  carriers: Carrier[];
}
*/

const CarrierList = ({ carriers, id, onCarrierSelect, readOnly }) => {
  const createMutation = useCarrierCreate();
  const updateMutation = useCarrierUpdate();
  const removeMutation = useCarrierRemove();
  const createTerminalMutation = useTerminalCreate();

  const [isEditing, setIsEditing] = useState(false);
  const [editableCarrier, setEditableCarrier] = useState();
  const [deletableCarrier, setDeletableCarrier] = useState();
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [nameFilter, setNameFilter] = useState('');

  const addTerminalFromCarrier = useCallback(
    (carrier) => {
      const newTerminal = {
        active: true,
        address1: carrier.address1,
        address2: carrier.address2,
        city: carrier.city,
        contactName: carrier.primaryContactName,
        email: carrier.primaryEmail,
        ext: carrier.primaryExt,
        id: 0,
        name: carrier.name,
        phone: carrier.primaryPhone,
        state: carrier.state,
        zipCode: carrier.zipCode,
      };
      createTerminalMutation.mutate(newTerminal);
    },
    [createTerminalMutation]
  );

  const onAdd = useCallback(
    (carrier) => {
      createMutation.mutate(carrier);

      if (carrier.isTerminal) {
        addTerminalFromCarrier(carrier);
      }
    },
    [addTerminalFromCarrier, createMutation]
  );

  const onUpdate = useCallback(
    (carrier) => {
      updateMutation.mutate(carrier);
      if (carrier.isTerminal) {
        addTerminalFromCarrier(carrier);
      }
    },
    [addTerminalFromCarrier, updateMutation]
  );

  const onDelete = useCallback(() => {
    if (deletableCarrier) {
      removeMutation.mutate(deletableCarrier.id);
    }
  }, [deletableCarrier, removeMutation]);

  const onEdit = useCallback((carrier) => {
    setEditableCarrier(carrier);
    setShowEditModal(true);
  }, []);

  const onRestore = useCallback(
    (carrier) => {
      updateMutation.mutate({ ...carrier, isDeleted: false });
    },
    [updateMutation]
  );

  useEffect(() => {
    if (!isEditing) {
      setDeletableCarrier(undefined);
    }
  }, [isEditing]);

  useEffect(() => {
    if (createMutation.isSuccess) {
      setShowAddModal(false);
    }
  }, [createMutation.isSuccess]);

  useEffect(() => {
    if (updateMutation.isSuccess) {
      setShowEditModal(false);
    }
  }, [updateMutation.isSuccess]);

  const columns = useMemo(
    () => [
      {
        dataIndex: 'name',
        render: (_id, carrier) => <>{carrier.name}</>,
        title: 'Carrier',
      },
      {
        dataIndex: 'address1',
        render: (_id, carrier) => {
          return (
            <>
              <span>{carrier.address1}</span>
              <br />
              {carrier.address2 && (
                <>
                  <span>{carrier.address2}</span>
                  <br />
                </>
              )}
              <span>
                {carrier.city}, {carrier.state} {carrier.zipCode}
              </span>
            </>
          );
        },
        title: 'Address',
      },
      {
        dataIndex: 'primaryContactName',
        render: (_id, carrier) => {
          return (
            <>
              <span>{carrier.primaryContactName}</span>
              <br />
              <PatternFormat
                displayType="text"
                format="###-###-####"
                value={carrier.primaryPhone}
              />
              <br />
              <span>{carrier.primaryEmail}</span>
            </>
          );
        },
        title: 'Primary Contact',
      },
      {
        dataIndex: 'secondaryContactName',
        render: (_id, carrier) => {
          return (
            <>
              <span>{carrier.secondaryContactName}</span>
              <br />
              <PatternFormat
                displayType="text"
                format="###-###-####"
                value={carrier.secondaryPhone}
              />
              <br />
              <span>{carrier.secondaryEmail}</span>
            </>
          );
        },
        title: 'Secondary Contact',
      },
      ...(readOnly
        ? [
          {
            dataIndex: 'id',
            render: (_id, carrier) => (
              <Button
                icon={<UndoOutlined />}
                loading={
                  updateMutation.isLoading &&
                  updateMutation.variables?.id === carrier.id
                }
                size="middle"
                onClick={() => onRestore(carrier)}
              >
                Restore
              </Button>
            ),
            title: 'Actions',
            width: 140,
          },
        ]
        : []),
      ...(isEditing && !readOnly
        ? [
          {
            dataIndex: 'id',
            render: (_id, carrier) => {
              if (isEditing) {
                return (
                  <Space>
                    <Button
                      icon={<EditOutlined />}
                      shape="circle"
                      size="small"
                      onClick={() => onEdit(carrier)}
                    />
                    <Popconfirm
                      okButtonProps={{ loading: removeMutation.isLoading }}
                      placement="leftBottom"
                      title="Are you sure to delete this carrier?"
                      visible={deletableCarrier?.id === carrier.id}
                      onCancel={() => setDeletableCarrier(undefined)}
                      onConfirm={onDelete}
                    >
                      <Button
                        danger
                        icon={<CloseOutlined />}
                        shape="circle"
                        size="small"
                        onClick={() => setDeletableCarrier(carrier)}
                      />
                    </Popconfirm>
                  </Space>
                );
              }
              return null;
            },
            title: 'Actions',
            width: 125,
          },
        ]
        : []),
    ],
    [
      deletableCarrier?.id,
      isEditing,
      onDelete,
      onEdit,
      onRestore,
      readOnly,
      removeMutation.isLoading,
      updateMutation.isLoading,
      updateMutation.variables?.id,
    ]
  );

  const filteredCarriers = useMemo(() => {
    if (!nameFilter.trim()) return carriers;
    const search = nameFilter.trim().toLowerCase();
    return carriers.filter((carrier) =>
      (carrier.name || '').toLowerCase().includes(search)
    );
  }, [carriers, nameFilter]);

  return (
    <Container id={id}>
      <div style={{ background: 'var(--bg-content)', position: 'sticky', top: 95, zIndex: 1 }}>
        <PanelHeader
          extra={
            !readOnly ? (
              <Space>
                <EditButton
                  color={isEditing ? '#389e0d' : undefined}
                  onClick={() => setIsEditing(!isEditing)}
                >
                  {isEditing ? 'Done' : 'Edit'}
                </EditButton>
                <Button
                  ghost
                  style={{ width: 120 }}
                  type="primary"
                  onClick={() => setShowAddModal(true)}
                >
                  Add Carrier
                </Button>
              </Space>
            ) : undefined
          }
          ghost={false}
          title={
            <Input
              allowClear
              placeholder="Search by carrier name"
              prefix={<SearchOutlined />}
              style={{ width: 210 }}
              value={nameFilter}
              onChange={(e) => setNameFilter(e.target.value)}
            />
          }
        />
      </div>

      {carriers.length ? (
        <CPTable
          className={readOnly ? 'carrier-list--inactive' : undefined}
          columns={columns}
          dataSource={filteredCarriers}
          pagination={false}
          rowKey={(carrier) => carrier.id}
          sticky={{ offsetHeader: 140 }}
        />
      ) : null}

      {showAddModal && (
        <CarrierModal
          error={createMutation.error}
          selectedCarrier={null}
          submitting={createMutation.isLoading}
          onClose={() => setShowAddModal(false)}
          onSubmit={onAdd}
        />
      )}
      {showEditModal && editableCarrier && (
        <CarrierModal
          error={updateMutation.error}
          selectedCarrier={editableCarrier}
          submitting={updateMutation.isLoading}
          onClose={() => setShowEditModal(false)}
          onSubmit={onUpdate}
        />
      )}
    </Container>
  );
};

export default CarrierList;
